import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { TenantDto } from '@nusign/picflow-backend-types';
import { firstValueFrom } from 'rxjs';
import { GROWTHBOOK } from '../../growthbook';
import { getAllRouteParams } from '../../lib/get-all-route-params';
import { getFullPath } from '../../lib/get-full-path';
import { GalleryPasswordService } from '../../services/gallery-password.service';
import { GalleryService } from '../../services/gallery.service';
import { GuestService } from '../../services/guest.service';
import { PermissionService } from '../../services/permission.service';
import { RedirectService } from '../../services/redirect.service';
import { SentryService } from '../../services/sentry.service';
import { StateService } from '../../services/state.service';
import { UserService } from '../../services/user.service';
import { VisitorService } from '../../services/visitor.service';
import { getBaseWithoutKey } from '../libs/base-without-key';

@Injectable({
  providedIn: 'root',
})
export class ViewGalleryGuard {
  private growthBook = inject(GROWTHBOOK);
  private galleryService = inject(GalleryService);
  private state = inject(StateService);
  private userService = inject(UserService);
  private redirectService = inject(RedirectService);
  private guestService = inject(GuestService);
  private galleryPasswordService = inject(GalleryPasswordService);
  private visitorService = inject(VisitorService);
  private sentryService = inject(SentryService);
  private permissionService = inject(PermissionService);
  private platformId = inject(PLATFORM_ID);

  async canActivate(next: ActivatedRouteSnapshot): Promise<UrlTree | boolean> {
    if (isPlatformBrowser(this.platformId)) {
      await firstValueFrom(this.userService.getUser());
      await this.guestService.getGuest();
    }
    const resp = await this.handleGalleryPath(next);

    if (resp === true) {
      const gallery = await this.galleryService.getPublicGalleryFromSnapshot(next);

      const tenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
      this.state.guestVisitingTenantId = tenantId;
      await firstValueFrom(this.permissionService.loadTenantBillingPermissions(tenantId));
      // const tenant = gallery['tenant']?.id ? (gallery['tenant'] as TenantDto) : undefined;

      this.growthBook.setAttributes({
        ...this.growthBook.getAttributes(),
        // $groups: getTenantGroups(tenant), // gallery.tenant doesn't currently include the onboarding_checklist which is needed for this
        tenantId,
      });

      if (next.queryParams.preview) {
        this.galleryService.fakeGalleryView(gallery.id);
        return true;
      }

      // If we are allowed to view this gallery, we will report a gallery view to the backend
      if (isPlatformBrowser(this.platformId)) {
        if (this.state.guest || this.state.user) {
          this.visitorService.createVisitor$.next(gallery.id);
        }
        try {
          await this.galleryService.galleryView(gallery.id);
          if (gallery.requires_user_identification && !this.state.tokenData && !this.state.guestAuthToken) {
            this.sentryService.captureException('no-token-for-gallery-with-identification-required');
          }
        } catch (err) {
          setTimeout(() => {
            // throwing error only after timeout because gallery view is non critical
            if (!(err instanceof HttpErrorResponse)) throw err;
            if (err.status !== 404 && err.status !== 403 && err.status !== 401) throw err;
            // Ignoring 404 errors here.. Seems that this just happens sometimes and I have no clue why
          });
        }
      }
    }

    return resp;
  }

  public async getGalleryPathAction(
    next: ActivatedRouteSnapshot,
    ignoreIdentity = false,
    idOrPath?: string
  ): Promise<true | 'identify' | 'not-found' | 'access' | 'maintenance' | 'offline' | 'error'> {
    if (!idOrPath) {
      const params = getAllRouteParams(next);
      idOrPath = params.galleryPath || params.galleryId;
    }
    try {
      const gallery = await this.galleryService.getPublicGalleryFromSnapshot(next);

      if (gallery.requires_user_identification && !ignoreIdentity) {
        const userLoggedIn = !!(await firstValueFrom(this.userService.getUser()));
        const guestLoggedIn = !!(await this.guestService.getGuest());
        if (!guestLoggedIn && !userLoggedIn) return 'identify';
      }

      return true;
    } catch (error) {
      if (!(error instanceof HttpErrorResponse)) throw error;
      if (error.status === 404 || error.status === 403) {
        this.state.nextPageIsNotFound = true;
        return 'not-found';
      }

      if (error.status === 401 || error.status === 403) {
        this.galleryPasswordService.removePassword(idOrPath);
        return 'access';
      }

      if (error.status === 503) {
        return 'maintenance';
      }

      if (error.status === 0) {
        return 'offline';
      }

      console.error('Error in getGalleryPathAction', error);

      return 'error';
    }
  }

  public async handleGalleryPath(next: ActivatedRouteSnapshot, ignoreIdentity = false) {
    const action = await this.getGalleryPathAction(next, ignoreIdentity);
    if (action === true) return true;
    return this.redirectToAction(next, action);
  }

  public redirectToAction(next: ActivatedRouteSnapshot, action: string) {
    if (action === 'offline') action = 'error';
    const idOrPath = next.params.galleryPath || next.params.galleryId;
    const fullPath = getFullPath(next); // ['EmeraCyborg', 'asset_TmYBWlq7rw2cI2qR'];
    const { baseWithoutKey, rest } = getBaseWithoutKey(fullPath, idOrPath);

    if (action === 'not-found') {
      const params = getAllRouteParams(next);
      if (params.mainGalleryPath) {
        return this.redirectService.redirectTo(
          [
            ...baseWithoutKey.filter(k => k !== params.mainGalleryPath),
            params.mainGalleryPath,
            ...rest.filter(k => k === 'advanced'),
          ],
          {
            queryParams: next.queryParams,
          }
        );
      }
    }

    return this.redirectService.redirectTo([...baseWithoutKey, action, ...rest], { queryParams: next.queryParams });
  }
}
